'use client';
import React from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import {
  BookOpen,
  LayoutDashboard,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Settings,
  Heart,
  Lightbulb,
  X,
  User,
  Globe,
  HelpCircle,
  BarChart2,
  SmilePlus,
  FileText,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { SUPPORTED_LANGUAGES } from '@/lib/i18n';
import Icon from '@/components/ui/AppIcon';


interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  onMobileClose?: () => void;
}

export default function Sidebar({ collapsed, onToggle, onMobileClose }: SidebarProps) {
  const pathname = usePathname();
  const router = useRouter();
  const { user, signOut } = useAuth();
  const { language, setLanguage, t } = useLanguage();

  const navItems = [
    { key: 'nav-checkin', label: t('nav.checkin'), href: '/', icon: BookOpen, badge: null },
    { key: 'nav-exercises', label: t('nav.exercises'), href: '/exercises', icon: Heart, badge: null },
    { key: 'nav-mood', label: 'Mood Tracker', href: '/mood-tracker', icon: SmilePlus, badge: null },
    { key: 'nav-insights', label: 'Insights', href: '/insights', icon: BarChart2, badge: null },
    { key: 'nav-guidance', label: t('nav.guidance'), href: '#', icon: Lightbulb, badge: null },
    { key: 'nav-progress', label: t('nav.progress'), href: '#', icon: LayoutDashboard, badge: null },
    { key: 'nav-profile', label: t('nav.profile'), href: '/profile', icon: User, badge: null },
    { key: 'nav-help', label: 'Help & FAQ', href: '/help', icon: HelpCircle, badge: null },
    { key: 'nav-techdocs', label: 'Tech Docs', href: '/tech-docs', icon: FileText, badge: null },
  ];

  const handleSignOut = async () => {
    try {
      await signOut();
      router.push('/sign-up-login-screen');
      router.refresh();
    } catch (err) {
      console.error('Sign out error:', err);
    }
  };

  const displayName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'User';
  const initials = displayName.slice(0, 2).toUpperCase();

  return (
    <aside
      className="h-full flex flex-col sidebar-transition overflow-hidden"
      style={{
        width: collapsed ? '64px' : '240px',
        background: 'linear-gradient(180deg, #003399 0%, #002580 100%)',
      }}
    >
      {/* Logo */}
      <div
        className="flex items-center justify-between px-4 py-5 min-h-[72px]"
        style={{ borderBottom: '1px solid rgba(255,255,255,0.12)' }}
      >
        {!collapsed && (
          <div className="flex items-center gap-2">
            <AppLogo size={140} variant="full" />
          </div>
        )}
        {collapsed && (
          <div className="flex items-center justify-center w-full">
            <AppLogo size={36} variant="emblem" />
          </div>
        )}
        {!collapsed && (
          <button
            onClick={onToggle}
            className="p-1.5 rounded-md transition-colors hidden lg:flex"
            style={{ color: 'rgba(255,255,255,0.6)' }}
            onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.1)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            aria-label="Collapse sidebar"
          >
            <ChevronLeft size={16} />
          </button>
        )}
        {onMobileClose && (
          <button
            onClick={onMobileClose}
            className="p-1.5 rounded-md transition-colors lg:hidden"
            style={{ color: 'rgba(255,255,255,0.6)' }}
            aria-label="Close menu"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {collapsed && (
        <button
          onClick={onToggle}
          className="hidden lg:flex items-center justify-center py-2 transition-colors"
          style={{ color: 'rgba(255,255,255,0.6)' }}
          aria-label="Expand sidebar"
        >
          <ChevronRight size={16} />
        </button>
      )}

      {/* Nav */}
      <nav className="flex-1 py-4 overflow-y-auto overflow-x-hidden">
        {!collapsed && (
          <p className="text-xs font-600 uppercase tracking-widest px-4 mb-2" style={{ color: 'rgba(255,255,255,0.4)' }}>
            My Space
          </p>
        )}
        <ul className="space-y-0.5">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = pathname === item.href;
            return (
              <li key={item.key}>
                <Link
                  href={item.href}
                  title={collapsed ? item.label : undefined}
                  onClick={onMobileClose}
                  className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-all duration-150 relative group"
                  style={
                    isActive
                      ? { background: 'rgba(227,174,84,0.18)', color: '#E3AE54', borderRight: '3px solid #E3AE54' }
                      : { color: 'rgba(255,255,255,0.75)' }
                  }
                  onMouseEnter={e => {
                    if (!isActive) {
                      (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.08)';
                      (e.currentTarget as HTMLElement).style.color = '#ffffff';
                    }
                  }}
                  onMouseLeave={e => {
                    if (!isActive) {
                      (e.currentTarget as HTMLElement).style.background = 'transparent';
                      (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.75)';
                    }
                  }}
                >
                  <Icon size={18} className="flex-shrink-0" />
                  {!collapsed && <span className="flex-1 whitespace-nowrap">{item.label}</span>}
                  {!collapsed && item.badge && (
                    <span className="text-xs font-600 px-1.5 py-0.5 rounded-full" style={{ background: '#E3AE54', color: '#003399' }}>
                      {item.badge}
                    </span>
                  )}
                  {collapsed && (
                    <span className="absolute left-full ml-2 px-2 py-1 bg-foreground text-background text-xs rounded-md opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50 transition-opacity">
                      {item.label}
                    </span>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>

        {/* Language toggle */}
        {!collapsed && (
          <div className="px-4 mt-4">
            <p className="text-xs font-600 uppercase tracking-widest mb-2" style={{ color: 'rgba(255,255,255,0.4)' }}>
              {t('lang.toggle')}
            </p>
            <div className="flex items-center gap-1 rounded-lg p-1" style={{ background: 'rgba(255,255,255,0.08)' }}>
              {SUPPORTED_LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => setLanguage(lang.code)}
                  className="flex-1 py-1.5 rounded-md text-xs font-600 transition-all duration-150"
                  style={
                    language === lang.code
                      ? { background: '#E3AE54', color: '#003399' }
                      : { color: 'rgba(255,255,255,0.65)' }
                  }
                >
                  {lang.code === 'en' ? 'EN' : 'RW'}
                </button>
              ))}
            </div>
          </div>
        )}

        {collapsed && (
          <div className="px-2 mt-3">
            <button
              onClick={() => setLanguage(language === 'en' ? 'rw' : 'en')}
              className="w-full flex items-center justify-center py-2 rounded-lg transition-colors group relative"
              style={{ color: 'rgba(255,255,255,0.6)' }}
              title={`Switch to ${language === 'en' ? 'Kinyarwanda' : 'English'}`}
            >
              <Globe size={16} />
              <span className="absolute left-full ml-2 px-2 py-1 bg-foreground text-background text-xs rounded-md opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50 transition-opacity">
                {language === 'en' ? 'Ikinyarwanda' : 'English'}
              </span>
            </button>
          </div>
        )}
      </nav>

      {/* Bottom */}
      <div style={{ borderTop: '1px solid rgba(255,255,255,0.12)' }} className="py-3">
        <Link
          href="#"
          title={collapsed ? t('nav.settings') : undefined}
          className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors group relative"
          style={{ color: 'rgba(255,255,255,0.6)' }}
          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.08)'; (e.currentTarget as HTMLElement).style.color = '#ffffff'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.6)'; }}
        >
          <Settings size={18} className="flex-shrink-0" />
          {!collapsed && <span>{t('nav.settings')}</span>}
          {collapsed && (
            <span className="absolute left-full ml-2 px-2 py-1 bg-foreground text-background text-xs rounded-md opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50 transition-opacity">
              {t('nav.settings')}
            </span>
          )}
        </Link>
        <button
          onClick={handleSignOut}
          title={collapsed ? t('nav.signout') : undefined}
          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors group relative"
          style={{ color: 'rgba(255,255,255,0.6)' }}
          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(239,68,68,0.15)'; (e.currentTarget as HTMLElement).style.color = '#fca5a5'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.6)'; }}
        >
          <LogOut size={18} className="flex-shrink-0" />
          {!collapsed && <span>{t('nav.signout')}</span>}
          {collapsed && (
            <span className="absolute left-full ml-2 px-2 py-1 bg-foreground text-background text-xs rounded-md opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50 transition-opacity">
              {t('nav.signout')}
            </span>
          )}
        </button>

        {!collapsed && (
          <div className="px-4 pt-3">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-700" style={{ background: 'rgba(227,174,84,0.25)', color: '#E3AE54' }}>
                {initials}
              </div>
              <div className="min-w-0">
                <p className="text-xs font-600 text-white truncate">{displayName}</p>
                <p className="text-xs truncate" style={{ color: 'rgba(255,255,255,0.5)' }}>
                  {user ? user.email : 'Guest'}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}